import { createElement, lazy, Suspense } from 'react';
import { usesDesignV2 } from '../features/design-v2/rollout';
import type { PageContext } from '../lib/types';
import { html } from '../lib/dom';
import { loadTable } from '../api/core';
import { listFlows } from '../api/flow-directory';
import { getProject } from '../api/workspace';
import { coverThumb } from '../components/thumb';
import { empty } from '../components/ui';

const LiveFlowAppPage = lazy(() => import('../features/design-v2/LiveFlowAppPage').then(module => ({ default: module.LiveFlowAppPage })));

let state: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
let loadError = '';

const appHref = (flow: any) => `#/projects/${flow.project_id}/flows/${flow.id}?mode=app`;

export function render(ctx: PageContext) {
  if (usesDesignV2(ctx)) return {
    title: 'Apps',
    content: createElement(Suspense, { fallback: createElement('div', { role: 'status', className: 'page' }, 'Loading apps…') }, createElement(LiveFlowAppPage, { context: ctx })),
  };
  const flows = listFlows({ workspaceId: ctx.workspaceId });
  const body = html`<div class="page flows-page">
    <header class="page-head"><div><div class="eyebrow">Generate Item</div><h1>Apps</h1><p class="lede">Choose a flow to run with its inputs and outputs.</p></div></header>
    ${state === 'error' ? html`<div class="banner error" role="alert">${loadError}<button class="btn sm" data-retry>Retry</button></div>`
      : state !== 'ready' ? empty('Loading flows…')
      : !flows.length ? empty('No flows are available in this workspace.')
      : html`<div class="flow-grid">${flows.map(flow => {
        const project = getProject(flow.project_id);
        return html`<a class="card flow-card hover" href="${appHref(flow)}">
          ${coverThumb(flow, 'thumb flow-cover')}
          <div class="flow-card-body">
            <div class="row"><strong class="flow-title ellipsis">${flow.name}</strong><span aria-hidden="true" class="mono">→</span></div>
            <div class="flow-context"><span class="ellipsis">${project?.name || 'Project'}</span></div>
            <p class="muted">Run flow</p>
          </div>
        </a>`;
      })}</div>`}
  </div>`;
  return { title: 'Apps', body, mount(root: HTMLElement) {
    const load = async () => {
      state = 'loading';
      try { await Promise.all([loadTable('flows'), loadTable('projects')]); state = 'ready'; }
      catch (cause: any) { state = 'error'; loadError = cause?.message || 'Could not load flows.'; }
      ctx.rerender();
    };
    if (state === 'idle') void load();
    root.querySelector('[data-retry]')?.addEventListener('click', () => { void load(); });
  } };
}

export function renderApp(ctx: PageContext) {
  if (usesDesignV2(ctx)) return {
    title: ctx.params.appId,
    content: createElement(Suspense, { fallback: createElement('div', { role: 'status', className: 'page' }, 'Loading app…') }, createElement(LiveFlowAppPage, { context: ctx })),
  };
  return { title: 'App', body: html`<div class="page"><div class="empty">App page not found. <a href="#/use-as-app">Back to Apps</a></div></div>` };
}
